import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Stack,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tabs, // Added
  Tab, // Added
  CircularProgress, // Added
  List, // Added
  ListItem, // Added
  ListItemButton, // Added
  ListItemIcon, // Added
  ListItemText, // Added
  AlertTitle // Added
} from '@mui/material';
import { styled, keyframes } from '@mui/system'; // Added keyframes
import CloseIcon from '@mui/icons-material/Close';
import AutoFixNormalIcon from '@mui/icons-material/AutoFixNormal'; // Icon for Magic Edit
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates'; // Icon for Suggestions tab
import EditIcon from '@mui/icons-material/Edit'; // Icon for Manual Edit tab
import RefreshIcon from '@mui/icons-material/Refresh'; // Icon for Refresh button
import TextFormatIcon from '@mui/icons-material/TextFormat';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import PaletteIcon from '@mui/icons-material/Palette';
import EditLocationAltIcon from '@mui/icons-material/EditLocationAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// Removed AutoFixHighIcon as translation hint is removed

// Keyframes for loading animation
const pulseAnimation = keyframes`
  0% { opacity: 0.6; transform: scale(0.95); }
  50% { opacity: 1; transform: scale(1.05); }
  100% { opacity: 0.6; transform: scale(0.95); }
`;

// Styled components (borrowed and adapted from other tool options)
const OptionsPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 45, 0.95)' : 'rgba(255, 255, 255, 0.95)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  backdropFilter: 'blur(10px)',
  width: '100%',
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
  animation: 'fadeIn 0.3s ease-out',
  maxHeight: '90vh',
  overflowY: 'auto',
  '@keyframes fadeIn': {
    '0%': { opacity: 0, transform: 'translateY(10px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' }
  }
}));

const OptionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.primary.main,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '& svg': { fontSize: '1.2rem' }
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  padding: 4,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.05)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)',
  },
  zIndex: 10
}));

const SectionBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.15)' : 'rgba(0, 0, 0, 0.03)',
  borderRadius: theme.spacing(1.5),
  marginTop: theme.spacing(1.5)
}));

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  width: '100%',
  display: 'flex', // Ensure flex display
  flexWrap: 'wrap', // Allow wrapping
  gap: theme.spacing(1), // Add gap between buttons (horizontally and vertically when wrapped)
  '& .MuiToggleButtonGroup-grouped': {
    // flexGrow: 1, // Remove flexGrow to allow natural width
    border: `1px solid ${theme.palette.divider} !important`,
    borderRadius: `${theme.shape.borderRadius}px !important`,
    // Remove margin adjustments, rely on gap
    // '&:not(:first-of-type)': {
    //   marginLeft: theme.spacing(1), // Removed
    // },
    '&:not(:first-of-type)': { // Ensure border radius is correct when wrapped
      marginLeft: theme.spacing(1),
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.text.primary,
      fontWeight: 600,
    },
  },
}));

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  textTransform: 'none',
  fontSize: '0.8rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '2px',
  minHeight: '50px', // Ensure consistent height
  flexBasis: 'auto', // Allow button to size based on content
  flexGrow: 0, // Ensure it doesn't grow unnecessarily
}));

const PromptPreviewBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: theme.spacing(1),
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  color: theme.palette.text.secondary,
  minHeight: '40px',
  display: 'flex',
  alignItems: 'center',
  wordBreak: 'break-word',
}));

// Styled Tab
const StyledTab = styled(Tab)(({ theme }) => ({
  minWidth: 'auto',
  flexGrow: 1,
  textTransform: 'none',
}));

// Styled List Item for suggestions
const SuggestionListItem = styled(ListItemButton)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(0.5),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  transition: 'background-color 0.2s ease-in-out',
}));


const MagicEditToolOptions = ({ settings, onChange, onApply, currentImage }) => { // Added currentImage prop
  const [tabValue, setTabValue] = useState(0); // 0 for Suggestions, 1 for Manual
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [suggestedEdits, setSuggestedEdits] = useState([]);
  const [analysisTrigger, setAnalysisTrigger] = useState(0); // State to manually trigger analysis

  // State for Manual Edit Tab
  const [action, setAction] = useState('add');
  const [objectToAdd, setObjectToAdd] = useState('');
  const [locationToAdd, setLocationToAdd] = useState('');
  const [styleToAdd, setStyleToAdd] = useState('');
  const [objectToRemove, setObjectToRemove] = useState('');
  const [objectToReplace, setObjectToReplace] = useState('');
  const [replaceWith, setReplaceWith] = useState('');
  const [objectToChange, setObjectToChange] = useState('');
  const [newColorMaterial, setNewColorMaterial] = useState('');
  const [areaToModify, setAreaToModify] = useState('');
  const [desiredChange, setDesiredChange] = useState('');
  // Removed fine-tuning state

  // Styles for optional selection (used in Manual tab)
  const manualStyles = [
    { value: '', label: 'Varsayılan' },
    { value: 'modern', label: 'Modern' },
    { value: 'vintage', label: 'Vintage' },
    { value: 'minimalist', label: 'Minimalist' },
    { value: 'rustic', label: 'Rustik' },
    { value: 'bohemian', label: 'Bohem' },
    { value: 'industrial', label: 'Endüstriyel' },
  ];

  // Generate the prompt based on the selected action and inputs
  const generatedPrompt = useMemo(() => {
    let prompt = '';
    switch (action) {
      case 'add':
        prompt = `Add a ${styleToAdd ? styleToAdd + ' ' : ''}${objectToAdd || '[object]'}${locationToAdd ? ' ' + locationToAdd : ''}.`;
        break;
      case 'remove':
        prompt = `Remove the ${objectToRemove || '[object]'}.`;
        break;
      case 'replace':
        prompt = `Replace the ${objectToReplace || '[object]'} with a ${replaceWith || '[new object]'}.`;
        break;
      case 'change':
        prompt = `Change the ${objectToChange || '[object/area]'} to ${newColorMaterial || '[color/material]'}.`;
        break;
      case 'modify':
        prompt = `Modify the ${areaToModify || '[area]'} to ${desiredChange || '[description]'}.`;
        break;
      default:
        prompt = '';
    }
    // Basic cleanup, remove placeholders before checking validity
    return prompt.replace(/\[object\]/g, '')
                 .replace(/\[new object\]/g, '')
                 .replace(/\[object\/area\]/g, '')
                 .replace(/\[color\/material\]/g, '')
                 .replace(/\[area\]/g, '')
                 .replace(/\[description\]/g, '')
                 .replace(/\s{2,}/g, ' ') // Replace multiple spaces with one
                 .trim();
  }, [action, objectToAdd, locationToAdd, styleToAdd, objectToRemove, objectToReplace, replaceWith, objectToChange, newColorMaterial, areaToModify, desiredChange]);

  // Trigger analysis when component mounts, tab switches to suggestions, or refresh is triggered
  useEffect(() => {
    const analyze = async () => {
      // Only analyze if on the suggestions tab and we have an image
      if (currentImage && tabValue === 0) {
        setIsAnalyzing(true);
        setAnalysisError(null);
        // Keep existing suggestions while analyzing? Or clear them? Let's clear for now.
        setSuggestedEdits([]);
        try {
          // Import API function locally to avoid potential circular dependencies if moved
          const { analyzeImageForEdits } = await import('../../api/geminiApi');
          // TODO: Pass context like room type if available/useful
          const suggestions = await analyzeImageForEdits(currentImage);
          setSuggestedEdits(suggestions);
        } catch (error) {
          console.error("Analysis failed:", error);
          setAnalysisError(error.message || "Görsel analiz edilirken bir hata oluştu.");
        } finally {
          setIsAnalyzing(false);
        }
      }
    };
    analyze();
    // --- IMPORTANT: Removed currentImage from dependencies ---
    // Only re-run analysis if the tab changes to 0 or the trigger count changes.
    // The check `if (currentImage && tabValue === 0)` inside the effect handles the initial load case.
  }, [tabValue, analysisTrigger]); // REMOVED currentImage dependency

  // Update parent state (ImageEditor) only when manual prompt changes
  useEffect(() => {
    if (tabValue === 1) { // Only update parent from manual tab changes
       onChange({ prompt: generatedPrompt });
    }
    // No need to update parent from suggestions tab until a suggestion is clicked
  }, [generatedPrompt, tabValue, onChange]);


  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Clear manual prompt state in parent when switching away from manual tab? Optional.
    // if (newValue !== 1) {
    //   onChange({ prompt: '' });
    // }
  };

  const handleActionChange = (event, newAction) => {
    if (newAction !== null) {
      setAction(newAction);
    }
    // Removed extra closing brace here
  };

  // Check if manual apply button should be disabled
  const isManualApplyDisabled = useMemo(() => {
    // Check if the generated prompt (from manual inputs) is empty or still contains placeholders implicitly
    return !generatedPrompt ||
           (action === 'add' && !objectToAdd) ||
           (action === 'remove' && !objectToRemove) ||
           (action === 'replace' && (!objectToReplace || !replaceWith)) ||
           (action === 'change' && (!objectToChange || !newColorMaterial)) ||
           (action === 'modify' && (!areaToModify || !desiredChange));
  }, [generatedPrompt, action, objectToAdd, objectToRemove, objectToReplace, replaceWith, objectToChange, newColorMaterial, areaToModify, desiredChange]);

  // Handle applying a suggestion from Tab 1
  const handleSuggestionClick = (suggestionPrompt) => {
    // Remove the clicked suggestion from the list *before* applying
    setSuggestedEdits(prevEdits => prevEdits.filter(edit => edit !== suggestionPrompt));

    if (typeof onApply === 'function') {
      console.log("Applying suggestion:", suggestionPrompt);
      onApply(true, suggestionPrompt); // Pass true and the suggestion prompt directly
      // Do NOT trigger re-analysis here
    }
  };

  // Handle applying the manually constructed prompt from Tab 2
  const handleApplyManual = () => {
    if (!isManualApplyDisabled && typeof onApply === 'function') {
      console.log("Applying manual prompt:", generatedPrompt);
      onApply(true, generatedPrompt); // Pass true and the manually constructed prompt
    }
  };


  const renderManualActionFields = () => {
    switch (action) {
      case 'add':
        return (
          <Stack spacing={1.5}>
            <TextField
              fullWidth
              size="small"
              label="Eklenecek Nesne"
              value={objectToAdd}
              onChange={(e) => setObjectToAdd(e.target.value)}
              placeholder="örn. modern bir yer lambası, küçük ahşap masa"
            />
            <TextField
              fullWidth
              size="small"
              label="Konum (isteğe bağlı)"
              value={locationToAdd}
              onChange={(e) => setLocationToAdd(e.target.value)}
              placeholder="örn. köşeye, kanepenin yanına"
            />
            <FormControl fullWidth size="small">
              <InputLabel id="style-add-label">Stil (isteğe bağlı)</InputLabel>
              <Select
                labelId="style-add-label"
                value={styleToAdd}
                label="Stil (isteğe bağlı)"
                onChange={(e) => setStyleToAdd(e.target.value)}
              >
                {manualStyles.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        );
      case 'remove':
        return (
          <TextField
            fullWidth
            size="small"
            label="Kaldırılacak Nesne"
            value={objectToRemove}
            onChange={(e) => setObjectToRemove(e.target.value)}
            placeholder="örn. kırmızı koltuk, duvardaki tablo"
          />
        );
      case 'replace':
        return (
          <Stack spacing={1.5}>
            <TextField
              fullWidth
              size="small"
              label="Değiştirilecek Nesne"
              value={objectToReplace}
              onChange={(e) => setObjectToReplace(e.target.value)}
              placeholder="örn. sehpa"
            />
            <TextField
              fullWidth
              size="small"
              label="Yeni Nesne"
              value={replaceWith}
              onChange={(e) => setReplaceWith(e.target.value)}
              placeholder="örn. yuvarlak cam masa"
            />
          </Stack>
        );
      case 'change':
        return (
          <Stack spacing={1.5}>
            <TextField
              fullWidth
              size="small"
              label="Nesne/Alan"
              value={objectToChange}
              onChange={(e) => setObjectToChange(e.target.value)}
              placeholder="örn. perdeler, duvar, kanepe"
            />
            <TextField
              fullWidth
              size="small"
              label="Yeni Renk/Materyal"
              value={newColorMaterial}
              onChange={(e) => setNewColorMaterial(e.target.value)}
              placeholder="örn. mavi, ahşap doku, mermer"
            />
          </Stack>
        );
      case 'modify':
        return (
          <Stack spacing={1.5}>
            <TextField
              fullWidth
              size="small"
              label="Değiştirilecek Alan"
              value={areaToModify}
              onChange={(e) => setAreaToModify(e.target.value)}
              placeholder="örn. gökyüzü, zemin, arka plan"
            />
            <TextField
              fullWidth
              size="small"
              label="İstenen Değişiklik"
              value={desiredChange}
              onChange={(e) => setDesiredChange(e.target.value)}
              placeholder="örn. bulutlu yap, ahşap doku ekle, bulanıklaştır"
            />
          </Stack>
        );
      default:
        return null;
    }
  };

  return (
    <OptionsPaper elevation={4}>
      <Box sx={{ position: 'relative' }}>
        <CloseButton onClick={() => onApply(false)}>
          <CloseIcon fontSize="small" />
        </CloseButton>

        <Stack spacing={1.5}> {/* Reduced spacing */}
          <Box>
            <OptionTitle>
              <AutoFixNormalIcon /> Sihirli Düzenleme
            </OptionTitle>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              AI önerileriyle veya manuel olarak görseli düzenleyin.
            </Typography>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', width: '100%' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="magic edit tabs"
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
            >
              <StyledTab icon={<TipsAndUpdatesIcon />} iconPosition="start" label="AI Önerileri" />
              <StyledTab icon={<EditIcon />} iconPosition="start" label="Manuel Düzenleme" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          <Box sx={{ pt: 1.5 }}> {/* Add padding top for tab content */}
            {/* AI Suggestions Tab */}
            {tabValue === 0 && (
              <Stack spacing={1.5}>
                {/* Refresh Button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                   <Button
                     size="small"
                     startIcon={<RefreshIcon />}
                     onClick={() => setAnalysisTrigger(prev => prev + 1)} // Increment trigger to re-run useEffect
                     disabled={isAnalyzing}
                   >
                     Önerileri Yenile
                   </Button>
                 </Box>

                {isAnalyzing && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                    <CircularProgress size={30} sx={{ mb: 1.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      Görsel analiz ediliyor...
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      AI düzenleme önerileri hazırlıyor.
                    </Typography>
                  </Box>
                )}
                {analysisError && !isAnalyzing && (
                  <Alert severity="error" variant="outlined">
                    <AlertTitle>Analiz Hatası</AlertTitle>
                    {analysisError}
                  </Alert>
                )}
                {!isAnalyzing && !analysisError && suggestedEdits.length === 0 && (
                   <Alert severity="info" variant="outlined" icon={<InfoOutlinedIcon/>}>
                     Bu görsel için otomatik düzenleme önerisi bulunamadı. Manuel düzenlemeyi deneyebilirsiniz.
                   </Alert>
                )}
                {!isAnalyzing && !analysisError && suggestedEdits.length > 0 && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                      Önerilen Düzenlemeler:
                    </Typography>
                    <List dense disablePadding>
                      {suggestedEdits.map((suggestion, index) => (
                        <SuggestionListItem
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5, color: 'primary.main' }}>
                             <AutoFixNormalIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                             primaryTypographyProps={{ variant: 'body2' }}
                             primary={suggestion}
                          />
                        </SuggestionListItem>
                      ))}
                    </List>
                     <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                       Bir öneriye tıklayarak doğrudan uygulayın (3 Kredi).
                     </Typography>
                  </Box>
                )}
              </Stack>
            )}

            {/* Manual Edit Tab */}
            {tabValue === 1 && (
              <Stack spacing={2}>
                {/* Action Selection */}
                <Box>
                  <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 500 }}>
                    1. Yapılacak İşlemi Seçin:
                  </Typography>
                  <StyledToggleButtonGroup
                    value={action}
                    exclusive
                    onChange={handleActionChange}
                    aria-label="edit action"
                    size="small"
                  >
                    <StyledToggleButton value="add" aria-label="add object">
                      <AddPhotoAlternateIcon fontSize="small" /> Ekle
                    </StyledToggleButton>
                    <StyledToggleButton value="remove" aria-label="remove object">
                      <RemoveCircleOutlineIcon fontSize="small" /> Kaldır
                    </StyledToggleButton>
                    <StyledToggleButton value="replace" aria-label="replace object">
                      <SwapHorizIcon fontSize="small" /> Değiştir
                    </StyledToggleButton>
                    <StyledToggleButton value="change" aria-label="change color/material">
                      <PaletteIcon fontSize="small" /> Renk/Doku
                    </StyledToggleButton>
                    <StyledToggleButton value="modify" aria-label="modify area">
                      <EditLocationAltIcon fontSize="small" /> Alanı Değiştir
                    </StyledToggleButton>
                  </StyledToggleButtonGroup>
                </Box>

                {/* Contextual Fields */}
                <SectionBox>
                  <Typography variant="caption" display="block" sx={{ mb: 1, fontWeight: 500 }}>
                    2. Detayları Belirtin:
                  </Typography>
                  {renderManualActionFields()}
                </SectionBox>

                {/* Prompting Tips */}
                <Accordion sx={{ boxShadow: 'none', backgroundColor: 'transparent', '&:before': { display: 'none' } }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="prompt-tips-content"
                    id="prompt-tips-header"
                    sx={{ minHeight: 'auto', padding: 0, '& .MuiAccordionSummary-content': { margin: 0 } }}
                  >
                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                      <InfoOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} /> İstek İpuçları
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 0, pt: 1 }}>
                    <Alert severity="info" icon={false} variant="outlined" sx={{ fontSize: '0.8rem', padding: '4px 8px' }}>
                      - Nesneleri spesifik olarak tanımlayın (örn. 'uzun yeşil vazo' yerine 'vazo').
                      <br />
                      - İstenen stili veya rengi net bir şekilde belirtin.
                      <br />
                      - Sonuçlar yapay zekanın anlayışına bağlıdır. Gerekirse farklı ifadeler deneyin.
                    </Alert>
                  </AccordionDetails>
                </Accordion>

                {/* Apply Button for Manual Edit */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleApplyManual}
                    disabled={isManualApplyDisabled}
                    size="medium"
                    // Apply same styling as Virtual Staging button
                    sx={{
                      // background: 'linear-gradient(45deg, #8A2BE2 30%, #9370DB 90%)', // Example gradient
                      // boxShadow: '0 3px 5px 2px rgba(138, 43, 226, .3)' // Example shadow
                      // Using default primary button style for consistency unless specified otherwise
                    }}
                  >
                    Düzenle (3 Kredi)
                  </Button>
                </Box>
              </Stack>
            )}
          </Box>
        </Stack>
      </Box>
    </OptionsPaper>
  );
};

export default MagicEditToolOptions;
